import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getMesEtapes, updateEtape } from "../services/api";
import IconeV2 from "../components/IconeV2";
import { LIBELLES_ROLE_PROJET, formatDate, formatHeures, largeurJauge } from "../services/projet";

// ===================================================================
// The Crowd v2 — Mon activite › Mes etapes
//
// Refonte du module Projets : les etapes non terminees de tous mes
// projets (acteur principal ou secondaire, distingues), triees par
// echeance. L'acteur principal modifie l'avancement sur place ; a 100 %
// l'etape est terminee et quitte la liste (et la saisie des temps).
// Bascule Cartes / Liste comme Taches, choix memorise.
// ===================================================================

const VUE_STORAGE_KEY = "thecrowd-v2-vue-mes-etapes";

function vueInitiale() {
  try {
    const v = window.localStorage.getItem(VUE_STORAGE_KEY);
    if (v === "cartes" || v === "liste") return v;
  } catch {
    // stockage indisponible : vue par defaut
  }
  return "cartes";
}

function Jauge({ valeur }) {
  return (
    <div className="v2-projet-jauge">
      <span className="v2-barres-piste">
        <span className="v2-barres-valeur v2-ton-fort" style={{ width: largeurJauge(valeur) }} />
      </span>
      <span className="v2-projet-jauge-valeur">{valeur || 0} %</span>
    </div>
  );
}

// Avancement modifiable (acteur principal) : curseur, puis Enregistrer
function EditeurAvancement({ etape, onEnregistrer }) {
  const [valeur, setValeur] = useState(etape.avancement || 0);
  const [enCours, setEnCours] = useState(false);
  const modifie = Number(valeur) !== Number(etape.avancement || 0);

  useEffect(() => {
    setValeur(etape.avancement || 0);
  }, [etape.avancement]);

  const enregistrer = async () => {
    setEnCours(true);
    try {
      await onEnregistrer(etape, Number(valeur));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="v2-mes-avancement">
      <input type="range" min="0" max="100" step="5" className="v2-curseur" value={valeur}
        onChange={(e) => setValeur(e.target.value)} aria-label={`Avancement de ${etape.nom}`} disabled={enCours} />
      <span className="v2-projet-jauge-valeur">{valeur} %</span>
      {modifie && (
        <button type="button" className="v2-btn v2-btn-principal v2-btn-compact" onClick={enregistrer} disabled={enCours}>
          {enCours ? "…" : "Enregistrer"}
        </button>
      )}
    </div>
  );
}

function Avancement({ etape, onEnregistrer }) {
  return etape.peut_modifier
    ? <EditeurAvancement etape={etape} onEnregistrer={onEnregistrer} />
    : <Jauge valeur={etape.avancement} />;
}

function Badges({ e }) {
  return (
    <>
      <span className={`v2-statut${e.mon_role === "principal" ? " v2-statut-fort" : ""}`}>
        {e.mon_role === "principal" ? "Principal" : "Secondaire"}
      </span>
      {e.en_retard && <span className="v2-statut v2-statut-danger">En retard</span>}
    </>
  );
}

// --- Vue cartes ---

function CarteEtape({ e, onOuvrirProjet, onEnregistrer }) {
  return (
    <article className="v2-tache">
      <div className="v2-tache-corps">
        <div className="v2-tache-entete">
          <h3 className="v2-tache-nom">{e.nom}</h3>
        </div>
        <div className="v2-tache-meta">
          <Badges e={e} />
          <button type="button" className="v2-lien-ligne v2-tache-projet" onClick={onOuvrirProjet} title={e.projet_nom}>
            {e.projet_nom}
          </button>
        </div>
        <div className="v2-tache-suivi">
          <Avancement etape={e} onEnregistrer={onEnregistrer} />
          <span className="v2-cellule-discrete">
            Échéance : {formatDate(e.date_fin_prevue)} · Mon temps : {formatHeures(e.mes_heures)}
          </span>
        </div>
      </div>
    </article>
  );
}

// --- Vue liste ---

function VueListe({ etapes, onOuvrirProjet, onEnregistrer }) {
  return (
    <div className="v2-panneau v2-tableau-conteneur">
      <table className="v2-tableau">
        <thead>
          <tr>
            <th scope="col">Étape</th>
            <th scope="col">Projet</th>
            <th scope="col">Rôle</th>
            <th scope="col">Échéance</th>
            <th scope="col" className="v2-mes-col-avancement">Avancement</th>
            <th scope="col" className="v2-num">Mon temps</th>
          </tr>
        </thead>
        <tbody>
          {etapes.map((e) => (
            <tr key={e.id}>
              <td className="v2-gras">{e.nom}</td>
              <td>
                <button type="button" className="v2-lien-ligne" onClick={() => onOuvrirProjet(e)}>{e.projet_nom}</button>
              </td>
              <td><span className="v2-mes-badges"><Badges e={e} /></span></td>
              <td className="v2-cellule-discrete">{formatDate(e.date_fin_prevue)}</td>
              <td><Avancement etape={e} onEnregistrer={onEnregistrer} /></td>
              <td className="v2-num">{formatHeures(e.mes_heures)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Page ---

function MesEtapesV2({ collaborateur }) {
  const navigate = useNavigate();
  const [etapes, setEtapes] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [vue, setVue] = useState(vueInitiale);

  useEffect(() => {
    try {
      window.localStorage.setItem(VUE_STORAGE_KEY, vue);
    } catch {
      // cf. vueInitiale
    }
  }, [vue]);

  const charger = useCallback(() => {
    if (!collaborateur) return Promise.resolve();
    setErreur(null);
    return getMesEtapes(collaborateur.id)
      .then(setEtapes)
      .catch((err) => setErreur(`Étapes non chargées : ${err.message}`));
  }, [collaborateur]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">Compte non configuré. Contactez votre manager.</div>
      </div>
    );
  }

  const ouvrirProjet = (e) => navigate(`/v2/projets/${e.projet_id}`);

  const enregistrer = async (etape, avancement) => {
    setMessage(null);
    try {
      await updateEtape(etape.id, { avancement }, collaborateur.id);
      setMessage({
        type: "succes",
        texte: avancement >= 100
          ? `« ${etape.nom} » est terminée : elle quitte la liste et la saisie des temps.`
          : `Avancement de « ${etape.nom} » enregistré : ${avancement} %.`,
      });
      await charger();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Enregistrement impossible : ${err.message}` });
    }
  };

  const liste = etapes || [];
  const enRetard = liste.filter((e) => e.en_retard).length;

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Mes étapes</h1>
        <p className="v2-sous-titre">
          Les étapes à mener sur mes projets, par échéance. {LIBELLES_ROLE_PROJET.principal} : avancement modifiable ici.
        </p>
      </header>

      <div className="v2-barre-outils">
        <span className="v2-total-semaine">
          {liste.length} étape{liste.length > 1 ? "s" : ""} en cours
          {enRetard > 0 && <span className="v2-texte-danger"> · {enRetard} en retard</span>}
        </span>
        <div className="v2-barre-outils-droite">
          <div className="v2-bascule" role="group" aria-label="Affichage">
            <button type="button" className={`v2-bascule-choix${vue === "cartes" ? " actif" : ""}`} aria-pressed={vue === "cartes"} onClick={() => setVue("cartes")}>
              <IconeV2 nom="tableau" taille={13} /> Cartes
            </button>
            <button type="button" className={`v2-bascule-choix${vue === "liste" ? " actif" : ""}`} aria-pressed={vue === "liste"} onClick={() => setVue("liste")}>
              <IconeV2 nom="liste" taille={13} /> Liste
            </button>
          </div>
        </div>
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}

      {!etapes && !erreur && <div className="v2-chargement">Chargement…</div>}

      {etapes && liste.length === 0 && (
        <div className="v2-panneau"><p className="v2-vide">Aucune étape en cours sur mes projets.</p></div>
      )}

      {etapes && liste.length > 0 && vue === "cartes" && (
        <div className="v2-tache-grille">
          {liste.map((e) => (
            <CarteEtape key={e.id} e={e} onOuvrirProjet={() => ouvrirProjet(e)} onEnregistrer={enregistrer} />
          ))}
        </div>
      )}

      {etapes && liste.length > 0 && vue === "liste" && (
        <VueListe etapes={liste} onOuvrirProjet={ouvrirProjet} onEnregistrer={enregistrer} />
      )}
    </div>
  );
}

export default MesEtapesV2;
